import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { latestComics, featuredComics, categories } from '@/mocks/comics';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const allComics = [...featuredComics, ...latestComics];
  const comic = allComics.find((c) => c.id === slug);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">
              作品未找到
            </h1>
            <Link
              to="/"
              className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categorySlug = categories.find((c) => c.name === comic.category)?.slug || '';
  const relatedComics = allComics
    .filter((c) => c.id !== comic.id && (c.category === comic.category || c.tags.some((t) => comic.tags.includes(t))))
    .slice(0, 4);

  const chapters = Array.from({ length: Math.min(comic.totalChapters, 20) }, (_, i) => ({
    number: comic.totalChapters - i,
    title: `${comic.title} 第${comic.totalChapters - i}话`,
    date: new Date(Date.now() - i * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  }));

  const jsonLdBreadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://ipv6c.com/' },
      { '@type': 'ListItem', position: 2, name: `${comic.category}漫画`, item: `http://ipv6c.com/category/${categorySlug}` },
      { '@type': 'ListItem', position: 3, name: comic.title, item: `http://ipv6c.com/article/${comic.id}` },
    ],
  };

  const jsonLdArticle = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: comic.title,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.updateDate,
    dateModified: comic.updateDate,
    image: comic.cover,
    publisher: { '@type': 'Organization', name: '韩漫画网', url: 'http://ipv6c.com/' },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://ipv6c.com/article/${comic.id}`,
    },
  };

  const bgColors = [
    'bg-primary-100/40',
    'bg-accent-100/40',
    'bg-secondary-100/40',
    'bg-background-200/60',
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdBreadcrumb) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdArticle) }}
      />
      <Navbar />

      <main className="pt-16">
        <section className="bg-background-100 py-4">
          <div className="w-full px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                首页
              </Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </span>
              <Link to={`/category/${categorySlug}`} className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                {comic.category}漫画
              </Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </span>
              <span className="text-foreground-800 whitespace-nowrap">{comic.title}</span>
            </nav>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              <div className="lg:w-2/3">
                <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-700 mb-4">
                  {comic.category}
                </span>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                  {comic.title}
                </h1>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                  {comic.description}
                </p>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8">
                  <span>作者：{comic.author}</span>
                  <span>状态：{comic.status}</span>
                  <span>评分：{comic.rating}</span>
                  <span>{comic.views.toLocaleString()} 阅读</span>
                </div>
                <div className="flex flex-wrap gap-2 mb-8">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mb-8">
                  <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                    章节列表
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[400px] overflow-y-auto">
                    {chapters.map((ch) => (
                      <Link
                        key={ch.number}
                        to={`/article/${comic.id}`}
                        className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-background-200 border border-background-200/70 transition-colors cursor-pointer"
                      >
                        <span className="text-sm text-foreground-800">{ch.title}</span>
                        <span className="text-xs text-foreground-500">{ch.date}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              <div className="lg:w-1/3">
                <div className="sticky top-20">
                  <div className="rounded-2xl overflow-hidden bg-accent-100/30 p-4">
                    <div className="relative overflow-hidden rounded-xl aspect-[3/4] mb-4">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <Link
                      to={`/article/${comic.id}`}
                      className="flex items-center justify-center w-full py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
                    >
                      <span className="w-5 h-5 flex items-center justify-center mr-2">
                        <i className="ri-book-open-line"></i>
                      </span>
                      开始阅读 第1话
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="flex items-center gap-2 mb-8">
                <span className="w-1.5 h-1.5 rounded-full bg-foreground-900"></span>
                <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-900">
                  猜你喜欢
                </h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((rc, index) => (
                  <Link
                    key={rc.id}
                    to={`/article/${rc.id}`}
                    className="group rounded-2xl overflow-hidden cursor-pointer block transition-transform duration-300 hover:-translate-y-1"
                  >
                    <div className={`${bgColors[index % bgColors.length]} p-3 md:p-4`}>
                      <div className="relative overflow-hidden rounded-xl aspect-[3/4]">
                        <img
                          src={rc.cover}
                          alt={rc.title}
                          loading="lazy"
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                    </div>
                    <div className="px-3 md:px-4 pb-3 md:pb-4 pt-2 bg-background-50">
                      <h4 className="text-sm md:text-base font-heading font-semibold text-foreground-900 line-clamp-1">
                        {rc.title}
                      </h4>
                      <span className="text-xs text-foreground-500">{rc.author}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}